import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useBranch } from '../contexts/BranchContext';
import { customerSegmentApi } from '../services/customerSegmentApi';
import { CustomerSegment } from '../types/CustomerSegment';

interface LeadStage {
  id: number;
  name: string;
  description: string;
  color: string;
  sortOrder: number;
  isDefault: boolean;
  isActive: boolean;
  isGlobal: boolean;
  segmentIds?: number[];
  segmentNames?: string;
  segmentCodes?: string;
  leadsCount: number;
  createdAt: string;
  updatedAt: string;
}

interface LeadStagesManagerProps {
  onClose: () => void;
  onStageUpdate?: () => void;
}

const LeadStagesManager: React.FC<LeadStagesManagerProps> = ({ onClose, onStageUpdate }) => {
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [stages, setStages] = useState<LeadStage[]>([]);
  const [segments, setSegments] = useState<CustomerSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingStage, setEditingStage] = useState<LeadStage | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [draggedStage, setDraggedStage] = useState<LeadStage | null>(null);
  const [showTipsPopover, setShowTipsPopover] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSegmentFilter, setSelectedSegmentFilter] = useState<number | 'global' | null>(null);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    isGlobal: true,
    segmentIds: [] as number[]
  });

  // Color options for stages
  const colorOptions = [
    { name: 'Blue', value: '#3B82F6' },
    { name: 'Green', value: '#10B981' },
    { name: 'Yellow', value: '#F59E0B' },
    { name: 'Red', value: '#EF4444' },
    { name: 'Purple', value: '#8B5CF6' },
    { name: 'Pink', value: '#EC4899' },
    { name: 'Indigo', value: '#6366F1' },
    { name: 'Gray', value: '#6B7280' },
    { name: 'Orange', value: '#F97316' },
    { name: 'Teal', value: '#14B8A6' }
  ];

  // Filter stages based on search and segment
  const filteredStages = stages.filter(stage => {
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesName = stage.name.toLowerCase().includes(query);
      const matchesDescription = stage.description?.toLowerCase().includes(query);
      const matchesSegmentNames = stage.segmentNames?.toLowerCase().includes(query);
      
      if (!matchesName && !matchesDescription && !matchesSegmentNames) {
        return false;
      }
    }

    // Segment filter
    if (selectedSegmentFilter !== null) {
      if (selectedSegmentFilter === 'global') {
        // Show only global stages
        return stage.isGlobal;
      } else {
        // Show only stages assigned to this segment
        if (stage.isGlobal) {
          return false;
        }
        if (!stage.segmentIds || !stage.segmentIds.includes(selectedSegmentFilter as number)) {
          return false;
        }
      }
    }

    return true;
  });

  // Load stages and segments
  const loadStages = async () => {
    try {
      setLoading(true);
      const [stagesRes, segmentsRes] = await Promise.all([
        api.get('/leads/stages?includeInactive=true&withSegments=true'),
        customerSegmentApi.getSegments(false) // Only active segments
      ]);
      
      if (stagesRes.data.success) {
        setStages(stagesRes.data.data);
      } else {
        setError('Failed to load stages');
      }
      
      if (segmentsRes.success) {
        setSegments(segmentsRes.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!branchLoading) {
      loadStages();
    }
  }, [currentBranch, isViewingAllBranches, branchLoading]);

  // Handle form changes
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3B82F6',
      isGlobal: true,
      segmentIds: []
    });
    setEditingStage(null);
    setShowAddForm(false);
  };

  // Initialize default stages
  const initializeDefaults = async () => {
    if (!currentBranch?.id) {
      setError('Please select a branch first');
      return;
    }

    if (!window.confirm('This will create default lead stages for the current branch. Continue?')) {
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/leads/stages/initialize-defaults', {
        branchId: currentBranch.id
      });
      
      if (response.data.success) {
        await loadStages();
        setError(null);
        alert(`Successfully created ${response.data.data.length} default lead stages!`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error initializing default stages');
    } finally {
      setSaving(false);
    }
  };

  // Start editing a stage
  const startEdit = (stage: LeadStage) => {
    setFormData({
      name: stage.name,
      description: stage.description || '',
      color: stage.color,
      isGlobal: stage.isGlobal || false,
      segmentIds: stage.segmentIds || []
    });
    setEditingStage(stage);
    setShowAddForm(false);
  };

  // Save stage (create or update)
  const saveStage = async () => {
    if (!formData.name.trim()) {
      setError('Stage name is required');
      return;
    }

    // Validate segment assignment
    if (!formData.isGlobal && (!formData.segmentIds || formData.segmentIds.length === 0)) {
      setError('Segment-specific stages must be assigned to at least one segment');
      return;
    }

    if (formData.isGlobal && formData.segmentIds && formData.segmentIds.length > 0) {
      setError('Global stages cannot be assigned to specific segments');
      return;
    }

    setSaving(true);
    try {
      let response;
      if (editingStage) {
        // Update existing stage
        response = await api.put(`/leads/stages/${editingStage.id}`, formData);
      } else {
        // Create new stage
        response = await api.post('/leads/stages', formData);
      }

      if (response.data.success) {
        await loadStages();
        resetForm();
        onStageUpdate?.();
        setError(null);
      } else {
        setError(response.data.message || 'Failed to save stage');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error saving stage');
    } finally {
      setSaving(false);
    }
  };

  // Delete stage
  const deleteStage = async (stage: LeadStage) => {
    if (stage.leadsCount > 0) {
      setError(`Cannot delete "${stage.name}" because it has ${stage.leadsCount} leads assigned to it`);
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the stage "${stage.name}"?`)) {
      return;
    }

    setSaving(true);
    try {
      const response = await api.delete(`/leads/stages/${stage.id}`);
      if (response.data.success) {
        await loadStages();
        onStageUpdate?.();
        setError(null);
      } else {
        setError(response.data.message || 'Failed to delete stage');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error deleting stage');
    } finally {
      setSaving(false);
    }
  };

  // Set default stage
  const setDefaultStage = async (stage: LeadStage) => {
    setSaving(true);
    try {
      const response = await api.post(`/leads/stages/${stage.id}/set-default`);
      if (response.data.success) {
        await loadStages();
        onStageUpdate?.();
        setError(null);
      } else {
        setError(response.data.message || 'Failed to set default stage');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error setting default stage');
    } finally {
      setSaving(false);
    }
  };

  // Toggle stage active status
  const toggleStageActive = async (stage: LeadStage) => {
    if (!stage.isActive && stage.leadsCount > 0) {
      setError(`Cannot deactivate "${stage.name}" because it has ${stage.leadsCount} leads assigned to it`);
      return;
    }

    setSaving(true);
    try {
      const response = await api.put(`/leads/stages/${stage.id}`, {
        ...stage,
        isActive: !stage.isActive
      });
      if (response.data.success) {
        await loadStages();
        onStageUpdate?.();
        setError(null);
      } else {
        setError(response.data.message || 'Failed to update stage');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error updating stage');
    } finally {
      setSaving(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e: React.DragEvent, stage: LeadStage) => {
    setDraggedStage(stage);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetStage: LeadStage) => {
    e.preventDefault();
    
    if (!draggedStage || draggedStage.id === targetStage.id) {
      setDraggedStage(null);
      return;
    }

    // Create new order based on drop position
    const activeStages = stages.filter(s => s.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
    const targetIndex = activeStages.findIndex(s => s.id === targetStage.id);
    const draggedIndex = activeStages.findIndex(s => s.id === draggedStage.id);

    // Remove the dragged stage from its current position
    const reorderedStages = activeStages.filter(s => s.id !== draggedStage.id);
    
    // Insert the dragged stage at the target position
    reorderedStages.splice(targetIndex > draggedIndex ? targetIndex - 1 : targetIndex, 0, draggedStage);

    // Update sort orders
    const stageOrders = reorderedStages.map((stage, index) => ({
      id: stage.id,
      sortOrder: index + 1
    }));

    setSaving(true);
    try {
      const response = await api.post('/leads/stages/reorder', { stageOrders });
      if (response.data.success) {
        await loadStages();
        onStageUpdate?.();
        setError(null);
      } else {
        setError(response.data.message || 'Failed to reorder stages');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error reordering stages');
    } finally {
      setSaving(false);
      setDraggedStage(null);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">Loading stages...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Manage Lead Stages</h2>
                <p className="text-sm text-gray-600 mt-1">Customize your sales pipeline stages</p>
              </div>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowTipsPopover(!showTipsPopover)}
                  className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 text-xs font-semibold"
                  title="Tips"
                  aria-label="Show tips"
                >
                  i
                </button>
                {showTipsPopover && (
                  <>
                    <div className="fixed inset-0 z-10" aria-hidden onClick={() => setShowTipsPopover(false)} />
                    <div className="absolute left-0 top-full mt-2 z-20 w-72 p-3 bg-blue-50 border border-blue-200 rounded-lg shadow-lg">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">💡 Tips</h4>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>• Drag and drop stages to reorder your pipeline</li>
                        <li>• The default stage is automatically assigned to new leads</li>
                        <li>• Stages with leads cannot be deleted - deactivate them instead</li>
                        <li>• Use colors to visually organize your pipeline stages</li>
                      </ul>
                    </div>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content - no overall scroll; left column scrolls, right column fixed */}
          <div className="flex-1 min-h-0 flex flex-col p-6">
          {/* Error Alert */}
          {error && (
            <div className="shrink-0 mb-4 p-3 bg-red-50 border border-red-300 rounded-lg">
              <div className="flex">
                <svg className="w-5 h-5 text-red-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-600">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="ml-auto text-red-400 hover:text-red-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-2 gap-6 min-h-0">
            {/* Left: Stage list - scrollable */}
            <div className="flex flex-col min-h-0 min-w-0">
              <div className="shrink-0 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="text-lg font-medium text-gray-900">Current Stages</h3>
                </div>

                {/* Search and Filter Controls */}
                <div className="space-y-2 mb-3">
                  {/* Search Input */}
                  <div className="relative">
                    <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search stages by name, description..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>

                  {/* Segment Filter */}
                  <select
                    value={selectedSegmentFilter === null ? '' : selectedSegmentFilter}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) {
                        setSelectedSegmentFilter(null);
                      } else if (value === 'global') {
                        setSelectedSegmentFilter('global');
                      } else {
                        setSelectedSegmentFilter(parseInt(value));
                      }
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Segments & Global Stages</option>
                    <option value="global">Global Stages Only</option>
                    {segments.map((segment) => (
                      <option key={segment.id} value={segment.id}>
                        {segment.name} ({segment.code})
                      </option>
                    ))}
                  </select>

                  {/* Active Filter Info */}
                  {(searchQuery || selectedSegmentFilter) && (
                    <div className="flex items-center justify-between text-xs text-gray-600 bg-blue-50 px-3 py-2 rounded">
                      <span>
                        Showing {filteredStages.filter(s => s.isActive).length} of {stages.filter(s => s.isActive).length} active stages
                      </span>
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedSegmentFilter(null);
                        }}
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Clear Filters
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto space-y-2">
                {filteredStages
                  .filter(stage => stage.isActive)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">No stages found matching your filters</p>
                      <button
                        onClick={() => {
                          setSearchQuery('');
                          setSelectedSegmentFilter(null);
                        }}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-800"
                      >
                        Clear filters
                      </button>
                    </div>
                  ) : (
                  filteredStages
                  .filter(stage => stage.isActive)
                  .sort((a, b) => a.sortOrder - b.sortOrder)
                  .map((stage) => (
                    <div
                      key={stage.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, stage)}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, stage)}
                      className={`p-3 border rounded-lg cursor-move hover:shadow-md transition-shadow ${draggedStage?.id === stage.id ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                            <span className="inline-block w-4 h-4 rounded-full mr-2" style={{ backgroundColor: stage.color }}></span>
                          </div>
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">{stage.name}</span>
                              {stage.isDefault && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs rounded-full">Default</span>
                              )}
                            </div>
                            {stage.description && <p className="text-sm text-gray-600">{stage.description}</p>}
                            <div className="flex items-center space-x-2 mt-1">
                              <p className="text-xs text-gray-500">{stage.leadsCount} leads</p>
                              {stage.isGlobal && <span className="text-xs text-blue-600">• Global</span>}
                              {!stage.isGlobal && stage.segmentCodes && (
                                <span className="text-xs text-purple-600">• Segments: {stage.segmentCodes}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-1">
                          {!stage.isDefault && (
                            <button onClick={() => setDefaultStage(stage)} disabled={saving} className="text-xs text-gray-500 hover:text-green-600 px-2 py-1 rounded" title="Set as default">Set Default</button>
                          )}
                          <button onClick={() => startEdit(stage)} disabled={saving} className="text-blue-600 hover:text-blue-800 p-1 rounded" title="Edit stage">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => deleteStage(stage)} disabled={saving || stage.leadsCount > 0} className="text-red-600 hover:text-red-800 p-1 rounded disabled:text-gray-400 disabled:cursor-not-allowed" title={stage.leadsCount > 0 ? 'Cannot delete stage with leads' : 'Delete stage'}>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              {stages.some(stage => !stage.isActive) && (
                <div className="mt-6">
                  <h4 className="text-md font-medium text-gray-700 mb-2">Inactive Stages</h4>
                  <div className="space-y-2">
                    {stages.filter(stage => !stage.isActive).map((stage) => (
                      <div key={stage.id} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="inline-block w-4 h-4 rounded-full" style={{ backgroundColor: stage.color }}></span>
                            <div>
                              <span className="font-medium text-gray-700">{stage.name}</span>
                              <p className="text-xs text-gray-500">{stage.leadsCount} leads</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-1">
                            <button onClick={() => toggleStageActive(stage)} disabled={saving} className="text-xs text-green-600 hover:text-green-800 px-2 py-1 rounded">Activate</button>
                            <button onClick={() => deleteStage(stage)} disabled={saving || stage.leadsCount > 0} className="text-red-600 hover:text-red-800 p-1 rounded disabled:text-gray-400">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right: Create and edit stage form or placeholder - scrollable like left */}
            <div className="flex flex-col min-h-0 min-w-0">
              {(showAddForm || editingStage) ? (
                <>
                <h3 className="shrink-0 text-lg font-medium text-gray-900 mb-4">
                  {editingStage ? 'Edit Stage' : 'Add New Stage'}
                </h3>
                <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                  {/* Stage Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stage Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter stage name"
                      maxLength={100}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional description"
                    />
                  </div>

                  {/* Color Picker */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Stage Color
                    </label>
                    <div className="grid grid-cols-5 gap-2">
                      {colorOptions.map((color) => (
                        <button
                          key={color.value}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, color: color.value }))}
                          className={`w-8 h-8 rounded-full border-2 ${
                            formData.color === color.value ? 'border-gray-800' : 'border-gray-300'
                          }`}
                          style={{ backgroundColor: color.value }}
                          title={color.name}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Segment Assignment */}
                  <div className="space-y-3 border-t pt-4">
                    <label className="block text-sm font-medium text-gray-700">
                      Stage Type
                    </label>
                    
                    {/* Global vs Segment-Specific */}
                    <div className="space-y-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.isGlobal}
                          onChange={() => setFormData(prev => ({ ...prev, isGlobal: true, segmentIds: [] }))}
                          className="h-4 w-4 text-blue-600"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Global Stage</span>
                          <p className="text-xs text-gray-500">Visible to all leads regardless of segment</p>
                        </div>
                      </label>

                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={!formData.isGlobal}
                          onChange={() => setFormData(prev => ({ ...prev, isGlobal: false }))}
                          className="h-4 w-4 text-blue-600"
                        />
                        <div>
                          <span className="text-sm font-medium text-gray-900">Segment-Specific Stage</span>
                          <p className="text-xs text-gray-500">Only visible to leads with selected segments</p>
                        </div>
                      </label>
                    </div>

                    {/* Segment Selector (only if not global) */}
                    {!formData.isGlobal && (
                      <div className="ml-6 mt-3">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Assign to Segments *
                        </label>
                        <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                          {segments.length === 0 ? (
                            <p className="text-sm text-gray-500">No customer segments available. Create segments first.</p>
                          ) : (
                            segments.map((segment) => (
                              <label key={segment.id} className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={formData.segmentIds.includes(segment.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setFormData(prev => ({
                                        ...prev,
                                        segmentIds: [...prev.segmentIds, segment.id]
                                      }));
                                    } else {
                                      setFormData(prev => ({
                                        ...prev,
                                        segmentIds: prev.segmentIds.filter(id => id !== segment.id)
                                      }));
                                    }
                                  }}
                                  className="h-4 w-4 text-blue-600 rounded"
                                />
                                <div className="flex items-center space-x-2">
                                  <div
                                    className="w-3 h-3 rounded-full"
                                    style={{ backgroundColor: segment.color }}
                                  />
                                  <span className="text-sm text-gray-900">{segment.name}</span>
                                  <span className="text-xs text-gray-500 font-mono">({segment.code})</span>
                                </div>
                              </label>
                            ))
                          )}
                        </div>
                        {!formData.isGlobal && formData.segmentIds.length === 0 && (
                          <p className="text-xs text-red-600 mt-1">Please select at least one segment</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Form Actions */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      onClick={resetForm}
                      disabled={saving}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={saveStage}
                      disabled={saving || !formData.name.trim()}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {saving && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      )}
                      {saving ? 'Saving...' : (editingStage ? 'Update Stage' : 'Add Stage')}
                    </button>
                  </div>
                </div>
                </div>
              </>
              ) : (
                <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg p-6 flex flex-col items-center justify-center min-h-[200px] text-center">
                  <p className="text-gray-500 text-sm mb-3">Create or edit a stage in this panel.</p>
                  <p className="text-gray-400 text-xs mb-4">Click the button below to add a stage, or the edit icon on a stage in the list.</p>
                  <button
                    type="button"
                    onClick={() => { resetForm(); setShowAddForm(true); }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                  >
                    Add New Stage
                  </button>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LeadStagesManager;
